import store from '@/store';
import { DateTime } from 'luxon';
import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';

export interface UserState {
  /** userName */
  userName: string;
}

/**
 * 認証関連の状態管理クラス
 * このクラスの状態はリロードしても状態が保持される
 */
@Module({
  name: 'user',
  namespaced: true,
  dynamic: true,
  store,
  // ↓リロードしても状態を保持する場合に、この1行で追加制御が必要
  preserveState: sessionStorage.getItem('sample-ui') !== null
})
export class User extends VuexModule implements UserState {
  userName = '';
  userEmail = '';
  userDepartmentId = '';
  userDepartmentName = '';
  userEmployeeId = '';
  userEmployeeName = '';
  isAdmin = false;
  isUserAdmin = false;
  isSalesManager = false;
  matterBranchKey = '';
  estimateBranchKey = '';
  customerBranchKey = '';
  digit = 5;
  isEstimateNumberManualEntry = false;

  /**
   * userNameを設定する
   *
   * @param userName HexabaseのuserName
   */
  @Mutation
  setUserName(userName: string) {
    this.userName = userName;
  }

  /**
   * userEmailを設定する
   *
   * @param userEmail HexabaseのuserEmail
   */
  @Mutation
  setUserEmail(userEmail: string) {
    this.userEmail = userEmail;
  }

  /**
   * userDepartmentIdを設定する
   *
   * @param userDepartmentId 社員マスタのdepartment_id
   */
  @Mutation
  setUserDepartmentId(userDepartmentId: string) {
    this.userDepartmentId = userDepartmentId;
  }

  /**
   * userDepartmentNameを設定する
   *
   * @param userDepartmentName 社員マスタのdepartment名
   */
  @Mutation
  setUserDepartmentName(userDepartmentName: string) {
    this.userDepartmentName = userDepartmentName;
  }

  /**
   * userEmployeeIdを設定する
   *
   * @param userEmployeeId 社員マスタのitem_id
   */
  @Mutation
  setUserEmployeeId(userEmployeeId: string) {
    this.userEmployeeId = userEmployeeId;
  }

  /**
   * userEmployeeNameを設定する
   *
   * @param userEmployeeName 社員マスタの社員名
   */
  @Mutation
  setUserEmployeeName(userEmployeeName: string) {
    this.userEmployeeName = userEmployeeName;
  }

  /**
   * isAdminを設定する
   *
   * @param isAdmin flg
   */
  @Mutation
  setIsAdmin(isAdmin: boolean) {
    this.isAdmin = isAdmin;
  }

  /**
   * isUserAdminを設定する
   *
   * @param isUserAdmin flg
   */
  @Mutation
  setIsUserAdmin(isUserAdmin: boolean) {
    this.isUserAdmin = isUserAdmin;
  }

  /**
   * isSalesManagerを設定する
   *
   * @param isSalesManager flg
   */
  @Mutation
  setIsSalesManager(isSalesManager: boolean) {
    this.isSalesManager = isSalesManager;
  }

  /**
   * matterBranchKeyを設定する
   *
   * @param matterBranchKey 案件番号プレフィックス
   */
  @Mutation
  setMatterBranchKey(matterBranchKey: string) {
    this.matterBranchKey = matterBranchKey;
  }

  /**
   * estimateBranchKeyを設定する
   *
   * @param estimateBranchKey 見積番号プレフィックス
   */
  @Mutation
  setEstimateBranchKey(estimateBranchKey: string) {
    this.estimateBranchKey = estimateBranchKey;
  }

  /**
   * customerBranchKeyを設定する
   *
   * @param customerBranchKey 顧客番号プレフィックス
   */
  @Mutation
  setCustomerBranchKey(customerBranchKey: string) {
    this.customerBranchKey = customerBranchKey;
  }

  /**
   * digitを設定する
   *
   * @param digit 連番桁数
   */
  @Mutation
  setDigit(digit: number) {
    this.digit = digit;
  }

  /**
   * isEstimateNumberManualEntryを設定する
   *
   * @param isEstimateNumberManualEntry flg
   */
  @Mutation
  setIsEstimateNumberManualEntry(isEstimateNumberManualEntry: boolean) {
    this.isEstimateNumberManualEntry = isEstimateNumberManualEntry;
  }

  /**
   * UserStateをリセットする
   */
  @Mutation
  clearUserState() {
    this.userName = '';
    this.userEmail = '';
    this.userDepartmentId = '';
    this.userEmployeeId = '';
    this.userDepartmentName = '';
    this.userEmployeeName = '';
    this.isAdmin = false;
    this.isUserAdmin = false;
    this.isSalesManager = false;
    this.matterBranchKey = '';
    this.estimateBranchKey = '';
    this.customerBranchKey = '';
    this.digit = 5;
    this.isEstimateNumberManualEntry = false;
  }

  /**
   * userNameを取得する
   *
   * @returns userName
   */
  public get getUserName(): string {
    return this.userName;
  }

  /**
   * userEmailを取得する
   *
   * @returns userName
   */
  public get getUserEmail(): string {
    return this.userEmail;
  }

  /**
   * userDepartmentIdを取得する
   *
   * @returns userDepartmentId
   */
  public get getUserDepartmentId(): string {
    return this.userDepartmentId;
  }

  /**
   * userEmployeeIdを取得する
   *
   * @returns userEmployeeId
   */
  public get getUserEmployeeId(): string {
    return this.userEmployeeId;
  }

  /**
   * userDepartmentNameを取得する
   *
   * @returns userDepartmentName
   */
  public get getUserDepartmentName(): string {
    return this.userDepartmentName;
  }

  /**
   * userEmployeeNameを取得する
   *
   * @returns userEmployeeName
   */
  public get getUserEmployeeName(): string {
    return this.userEmployeeName;
  }

  /**
   * isAdminを取得する
   *
   * @returns isAdmin
   */
  public get getIsAdmin(): boolean {
    return this.isAdmin;
  }

  /**
   * isUserAdminを取得する
   *
   * @returns isUserAdmin
   */
  public get getIsUserAdmin(): boolean {
    return this.isUserAdmin;
  }

  /**
   * isSalesManagerを取得する
   *
   * @returns isSalesManager
   */
  public get getIsSalesManager(): boolean {
    return this.isSalesManager;
  }

  /**
   * matterBranchKeyを取得する
   *
   * @returns matterBranchKey + 年月日6桁 + '-'
   */
  public get getMatterBranchKey(): string {
//    return this.matterBranchKey;
    return this.matterBranchKey + DateTime.local().toFormat('yyyyMMdd') + '-';
  }

  /**
   * estimateBranchKeyを取得する
   *
   * @returns estimateBranchKey
   */
  public get getEstimateBranchKey(): string {
//    return this.estimateBranchKey;
    return this.estimateBranchKey + DateTime.local().toFormat('yyyyMMdd') + '-';
  }

  /**
   * customerBranchKeyを取得する
   *
   * @returns customerBranchKey + 年月日6桁 + '-'
   */
  public get getCustomerBranchKey(): string {
//    return this.cutomerBranchKey;
    return this.customerBranchKey + DateTime.local().toFormat('yyyyMMdd') + '-';
  }

  /**
   * digitを取得する
   *
   * @returns digit
   */
  public get getDigit(): number {
    return this.digit;
  }

  /**
   * isEstimateNumberManualEntryを取得する
   *
   * @returns isEstimateNumberManualEntry
   */
  public get getIsEstimateNumberManualEntry(): boolean {
    return this.isEstimateNumberManualEntry;
  }
}

export default getModule(User);
